import { httpService } from '@/utils/httpService';
import type {
  SourceCreate,
  SourceUpdate,
  SourcesQueryParams,
  ConsentUpdateRequest,
  ContactUpdateRequest,
} from '@/types/api';
import { SourceResponse } from '@/types/sources';

export class SourcesService {
  // Basic CRUD operations
  async getSources(params: SourcesQueryParams = {}): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>('/sources/', { params });
  }

  async getSource(sourceId: string): Promise<SourceResponse> {
    return httpService.get<SourceResponse>(`/sources/${sourceId}`);
  }

  async createSource(source: SourceCreate): Promise<SourceResponse> {
    return httpService.post<SourceResponse>('/sources/', source);
  }

  async updateSource(
    sourceId: string,
    update: SourceUpdate
  ): Promise<SourceResponse> {
    return httpService.put<SourceResponse>(`/sources/${sourceId}`, update);
  }

  async deleteSource(sourceId: string): Promise<{ message: string }> {
    return httpService.delete<{ message: string }>(`/sources/${sourceId}`);
  }

  // Specialized queries
  async searchSources(
    query: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>('/sources/search', {
      params: { q: query, ...params },
    });
  }

  async getActiveSources(
    params: { skip?: number; limit?: number } = {}
  ): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>('/sources/active', { params });
  }

  async getSourcesByExpertise(
    expertiseArea: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>(
      `/sources/expertise/${expertiseArea}`,
      { params }
    );
  }

  async getSourcesByIndustry(
    industry: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>(`/sources/industry/${industry}`, {
      params,
    });
  }

  async getSourcesByCompany(
    company: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<SourceResponse[]> {
    return httpService.get<SourceResponse[]>(`/sources/company/${company}`, {
      params,
    });
  }

  // Consent and contact management
  async updateConsent(
    sourceId: string,
    consentData: ConsentUpdateRequest
  ): Promise<SourceResponse> {
    return httpService.patch<SourceResponse>(
      `/sources/${sourceId}/consent`,
      consentData
    );
  }

  async updateLastContact(
    sourceId: string,
    contactData: ContactUpdateRequest
  ): Promise<SourceResponse> {
    return httpService.patch<SourceResponse>(
      `/sources/${sourceId}/contact`,
      contactData
    );
  }

  async markAsDoNotContact(sourceId: string): Promise<SourceResponse> {
    return httpService.patch<SourceResponse>(
      `/sources/${sourceId}/do-not-contact`
    );
  }

  async verifySource(sourceId: string): Promise<SourceResponse> {
    return httpService.patch<SourceResponse>(`/sources/${sourceId}/verify`);
  }

  // Analytics and insights
  async getSourceEngagementStats(sourceId: string): Promise<any> {
    return httpService.get<any>(`/sources/${sourceId}/engagement-stats`);
  }

  async getSourceRecentActivity(
    sourceId: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<any[]> {
    return httpService.get<any[]>(`/sources/${sourceId}/recent-activity`, {
      params,
    });
  }
}

export const sourcesService = new SourcesService();
